(() => {
  'use strict';

  const HEADER = '[data-treedix-header-v2]';
  const FIRST_OVERFLOW = [4, 5, 6];
  const STAGES = [
    [],
    FIRST_OVERFLOW,
    [3, 4, 5, 6],
    [2, 3, 4, 5, 6],
    [1, 2, 3, 4, 5, 6]
  ];
  const SAFE = {
    logoToNav: 20,
    navToOverflow: 8,
    overflowToActions: 14,
    logoToActions: 12
  };
  const WIDTH_CHANGE_THRESHOLD = 4;
  const EXIT_OVERFLOW_BUFFER = 24;
  const SUPPRESS_FRAMES = 2;
  const MAX_STATE_CHANGES_PER_SECOND = 3;

  // Soft hysteresis thresholds only. Collision detection always wins.
  // Based on CSS viewport width, not browser zoom/devicePixelRatio.
  const OVERFLOW_ENABLE_MAX_WIDTH = 1280;
  const FULL_RESTORE_MIN_WIDTH = 1320;
  const LANGUAGE_LABELS = {
    en: { code: 'EN', name: 'English' },
    de: { code: 'DE', name: 'Deutsch' },
    es: { code: 'ES', name: 'Español' },
    fr: { code: 'FR', name: 'Français' },
    it: { code: 'IT', name: 'Italiano' },
    ja: { code: 'JA', name: '日本語' },
    nl: { code: 'NL', name: 'Nederlands' },
    pt: { code: 'PT-PT', name: 'Português' }
  };
  const mobileMedia = window.matchMedia('(max-width: 767px) and (hover: none) and (pointer: coarse)');
  const controllers = new WeakMap();

  function normalizeUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return (url.pathname || '/').replace(/\/$/, '') || '/';
    } catch (error) {
      return String(value || '').split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
    }
  }

  function currentLanguageLabel() {
    const htmlLang = String(document.documentElement.lang || '').toLowerCase();
    const pathLang = (window.location.pathname.match(/^\/([a-z]{2})(?:-[a-z]{2})?(?:\/|$)/i) || [])[1];
    const lang = (htmlLang || pathLang || 'en').slice(0, 2).toLowerCase();
    return LANGUAGE_LABELS[lang] || LANGUAGE_LABELS.en;
  }

  function syncMobileHextomLanguage(header) {
    if (!mobileMedia.matches) return;
    const drawerPanel = header.querySelector('.treedix-header-v2__drawer-panel');
    if (!drawerPanel) return;

    const language = currentLanguageLabel();
    drawerPanel.querySelectorAll('.ht-tms-dropdown__selected').forEach((selected) => {
      const items = selected.querySelectorAll('.tms-element-item');
      const codeItem = items[0];
      const nameItem = items[1];
      const codeText = codeItem && codeItem.querySelector('.tms-element-text');
      const nameText = nameItem && nameItem.querySelector('.tms-element-text');

      if (codeText && codeText.textContent.trim() !== language.code) {
        codeText.textContent = language.code;
      }
      if (nameText && nameText.textContent.trim() !== language.name) {
        nameText.textContent = language.name;
      }

      selected.setAttribute('aria-label', language.name);
    });
  }

  function scheduleMobileHextomLanguageSync(header) {
    syncMobileHextomLanguage(header);
    window.setTimeout(() => syncMobileHextomLanguage(header), 250);
    window.setTimeout(() => syncMobileHextomLanguage(header), 1000);
  }

  function nextFrame() {
    return new Promise((resolve) => window.requestAnimationFrame(resolve));
  }

  function afterFrames(count, callback) {
    if (count <= 0) {
      callback();
      return;
    }
    window.requestAnimationFrame(() => afterFrames(count - 1, callback));
  }

  function waitForLogo(header) {
    const images = Array.from(header.querySelectorAll('.treedix-header-v2__logo img'));
    const pending = images.filter((image) => !image.complete);
    if (!pending.length) return Promise.resolve();
    return Promise.all(pending.map((image) => new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    })));
  }

  function initHeader(header) {
    if (controllers.has(header)) return;

    const inner = header.querySelector('.treedix-header-v2__inner');
    const logo = header.querySelector('.treedix-header-v2__logo');
    const nav = header.querySelector('[data-header-v2-nav]');
    const visibleNav = header.querySelector('[data-header-v2-visible-nav]');
    const overflow = header.querySelector('[data-header-v2-overflow]');
    const overflowButton = header.querySelector('[data-header-v2-overflow-button]');
    const overflowMenu = header.querySelector('[data-header-v2-overflow-menu]');
    const actions = header.querySelector('[data-header-v2-actions]');
    const menuButton = header.querySelector('[data-header-v2-menu-button]');
    const drawer = header.querySelector('[data-header-v2-drawer]');
    const required = { inner, logo, nav, visibleNav, overflow, overflowButton, overflowMenu, actions, menuButton, drawer };
    const missing = Object.keys(required).filter((key) => !required[key]);

    if (missing.length) {
      header.dataset.headerState = 'full';
      header.classList.add('is-ready');
      console.warn('[TREEDIX Header v2] Missing required node(s); keeping full navigation.', missing);
      return;
    }

    const allItems = Array.from(visibleNav.querySelectorAll(':scope > [data-header-nav-item]'));
    if (!allItems.length) {
      header.dataset.headerState = 'full';
      header.classList.add('is-ready');
      console.warn('[TREEDIX Header v2] No navigation items found; keeping full state.');
      return;
    }

    allItems.forEach((item, index) => {
      item.dataset.originalIndex = String(index);
    });

    const stateChanges = [];
    const disposers = [];
    let observer = null;
    let resizeTimer = 0;
    let isCalculating = false;
    let suppressObserver = false;
    let scheduled = false;
    let destroyed = false;
    let lastAvailableWidth = 0;
    let lastState = header.dataset.headerState || 'full';

    function sortItems(items) {
      return items.sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex));
    }

    function externalWidth() {
      return Math.round(document.documentElement.clientWidth || window.innerWidth || 0);
    }

    function thresholdPrefersPriority(width) {
      if (lastState === 'priority' || lastState === 'menu') {
        return width < FULL_RESTORE_MIN_WIDTH;
      }
      return width <= OVERFLOW_ENABLE_MAX_WIDTH;
    }

    function setReady() {
      header.classList.add('is-ready');
    }

    function closeOverflow() {
      overflow.classList.remove('is-open');
      overflowButton.setAttribute('aria-expanded', 'false');
    }

    function closeDrawer() {
      drawer.hidden = true;
      menuButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('treedix-header-v2-drawer-open');
    }

    function openDrawer() {
      drawer.hidden = false;
      menuButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('treedix-header-v2-drawer-open');
      scheduleMobileHextomLanguageSync(header);
    }

    function closeSubmenus(scope) {
      (scope || header).querySelectorAll('.is-submenu-open').forEach((item) => {
        item.classList.remove('is-submenu-open');
        const button = item.querySelector(':scope > .treedix-header-v2__nav-row [data-header-v2-submenu-toggle]');
        if (button) button.setAttribute('aria-expanded', 'false');
      });
    }

    function restoreAllItems() {
      sortItems(allItems).forEach((item) => visibleNav.appendChild(item));
    }

    function applyActiveState() {
      const currentPath = normalizeUrl(window.location.href);
      header.querySelectorAll('a[href]').forEach((link) => {
        const linkPath = normalizeUrl(link.getAttribute('href'));
        const serverActive = link.getAttribute('data-header-v2-current') === 'true';
        const active = serverActive || linkPath === currentPath;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'page');
        else if (link.getAttribute('aria-current') === 'page') link.removeAttribute('aria-current');
      });

      header.querySelectorAll('.treedix-header-v2__nav-item, .treedix-header-v2__drawer-item').forEach((item) => {
        item.classList.toggle('is-active', Boolean(item.querySelector('a.is-active')));
      });

      overflow.classList.toggle('is-active', Boolean(overflowMenu.querySelector('.treedix-header-v2__nav-item.is-active')));
    }

    function setDistribution(overflowIndexes) {
      const overflowSet = new Set(overflowIndexes);
      restoreAllItems();
      overflowMenu.innerHTML = '';
      if (overflowIndexes.length) {
        sortItems(allItems.filter((_, index) => overflowSet.has(index))).forEach((item) => overflowMenu.appendChild(item));
      }
      overflow.hidden = overflowIndexes.length === 0;
      applyActiveState();
    }

    function applyLayout(result) {
      suppressObserver = true;
      closeOverflow();
      closeSubmenus(header);

      if (result.state === 'menu') {
        restoreAllItems();
        overflowMenu.innerHTML = '';
        overflow.hidden = true;
      } else {
        setDistribution(result.overflowIndexes || []);
      }

      header.dataset.headerState = result.state;
      header.dataset.visibleNav = sortItems(allItems.filter((_, index) => !(result.overflowIndexes || []).includes(index))).map((item) => item.textContent.replace(/\s+/g, ' ').trim()).join(' | ');
      header.dataset.overflowNav = sortItems(allItems.filter((_, index) => (result.overflowIndexes || []).includes(index))).map((item) => item.textContent.replace(/\s+/g, ' ').trim()).join(' | ');
      header.dataset.debugState = result.state;
      header.dataset.debugReason = result.reason || '';
      header.dataset.debugAvailableWidth = String(result.availableWidth || externalWidth());
      header.dataset.debugRequiredWidth = String(Math.round(result.requiredWidth || 0));
      header.dataset.debugVisibleCount = String(result.state === 'menu' ? 0 : allItems.length - (result.overflowIndexes || []).length);
      header.dataset.debugOverflowCount = String(result.state === 'menu' ? 0 : (result.overflowIndexes || []).length);
      header.dataset.debugOverflowEnableMaxWidth = String(OVERFLOW_ENABLE_MAX_WIDTH);
      header.dataset.debugFullRestoreMinWidth = String(FULL_RESTORE_MIN_WIDTH);
      header.dataset.debugThresholdPrefersPriority = String(thresholdPrefersPriority(result.availableWidth || externalWidth()));
      header.dataset.debugObserverSuppressed = 'true';

      if (lastState !== result.state) {
        stateChanges.push(Date.now());
        while (stateChanges.length && Date.now() - stateChanges[0] > 1000) stateChanges.shift();
      }
      header.dataset.debugStateChangesPerSecond = String(stateChanges.length);
      header.dataset.debugLayoutCount = String((Number(header.dataset.debugLayoutCount || 0) + 1));

      lastState = result.state;
      inner.style.visibility = '';
      setReady();

      afterFrames(SUPPRESS_FRAMES, () => {
        suppressObserver = false;
        header.dataset.debugObserverSuppressed = 'false';
      });
    }

    function hasRealDimensions() {
      const innerRect = inner.getBoundingClientRect();
      const logoRect = logo.getBoundingClientRect();
      const actionsRect = actions.getBoundingClientRect();
      return innerRect.width > 0 && logoRect.width > 0 && actionsRect.width > 0;
    }

    function getVisibleItemRects() {
      const visibleItems = Array.from(visibleNav.querySelectorAll(':scope > [data-header-nav-item]'));
      return {
        first: visibleItems[0] ? visibleItems[0].getBoundingClientRect() : null,
        last: visibleItems[visibleItems.length - 1] ? visibleItems[visibleItems.length - 1].getBoundingClientRect() : null,
        count: visibleItems.length
      };
    }

    function measureCandidate(overflowIndexes, buffer) {
      setDistribution(overflowIndexes);
      const hasOverflow = overflowIndexes.length > 0;
      const logoRect = logo.getBoundingClientRect();
      const actionsRect = actions.getBoundingClientRect();
      const innerRect = inner.getBoundingClientRect();
      const overflowRect = overflow.getBoundingClientRect();
      const visibleRects = getVisibleItemRects();
      const lastRight = hasOverflow ? overflowRect.right : (visibleRects.last ? visibleRects.last.right : 0);
      const requiredWidth = Math.ceil(inner.scrollWidth);

      header.dataset.debugViewportWidth = String(Math.round(window.innerWidth || 0));
      header.dataset.debugVisualViewportWidth = String(Math.round((window.visualViewport && window.visualViewport.width) || 0));
      header.dataset.debugHeaderClientWidth = String(Math.round(inner.clientWidth || 0));
      header.dataset.debugHeaderScrollWidth = String(Math.round(inner.scrollWidth || 0));
      header.dataset.debugLogoRight = String(Math.round(logoRect.right || 0));
      header.dataset.debugActionsLeft = String(Math.round(actionsRect.left || 0));
      header.dataset.debugVisibleScrollWidth = String(Math.round(visibleNav.scrollWidth || 0));
      header.dataset.debugVisibleClientWidth = String(Math.round(visibleNav.clientWidth || 0));
      header.dataset.debugOverflowWidth = String(Math.round(hasOverflow ? overflowRect.width : 0));

      if (!hasRealDimensions() || !visibleRects.first || !visibleRects.last) {
        return { safe: false, defer: true, requiredWidth, reason: 'defer-zero-dimension' };
      }

      const deficits = [
        inner.scrollWidth - inner.clientWidth - 1,
        actionsRect.right - innerRect.right - 1,
        logoRect.right - actionsRect.left + SAFE.logoToActions,
        logoRect.right + SAFE.logoToNav - visibleRects.first.left,
        lastRight + SAFE.overflowToActions - actionsRect.left
      ];

      if (hasOverflow) {
        deficits.push(visibleRects.last.right + SAFE.navToOverflow - overflowRect.left);
      }

      const maxDeficit = Math.max(0, ...deficits.filter((value) => Number.isFinite(value)));
      const safe = maxDeficit <= buffer;
      return {
        safe,
        defer: false,
        requiredWidth,
        deficit: Math.round(maxDeficit),
        reason: safe ? 'safe' : 'deficit-' + Math.round(maxDeficit)
      };
    }

    async function computeTarget() {
      const available = externalWidth();
      const restoreBuffer = lastState === 'menu' || lastState === 'priority' ? EXIT_OVERFLOW_BUFFER : 0;

      closeDrawer();
      closeOverflow();
      closeSubmenus(header);
      inner.style.visibility = 'hidden';
      header.dataset.headerState = 'measuring';
      header.dataset.debugReason = 'measuring';
      restoreAllItems();
      overflowMenu.innerHTML = '';
      overflow.hidden = true;
      await nextFrame();
      await nextFrame();

      const full = measureCandidate([], restoreBuffer);
      const thresholdPrefersPriorityNow = thresholdPrefersPriority(available);
      header.dataset.debugThresholdPrefersPriority = String(thresholdPrefersPriorityNow);

      if (full.defer) return { state: 'full', overflowIndexes: [], reason: full.reason, requiredWidth: full.requiredWidth, availableWidth: available };
      if (full.safe) return { state: 'full', overflowIndexes: [], reason: full.reason, requiredWidth: full.requiredWidth, availableWidth: available };

      let lastMeasure = full;
      for (let i = 1; i < STAGES.length; i += 1) {
        const indexes = STAGES[i];
        await nextFrame();
        const candidate = measureCandidate(indexes, lastState === 'menu' ? EXIT_OVERFLOW_BUFFER : 0);
        lastMeasure = candidate;
        if (candidate.defer) return { state: 'full', overflowIndexes: [], reason: candidate.reason, requiredWidth: candidate.requiredWidth, availableWidth: available };
        if (candidate.safe) {
          const reasonPrefix = thresholdPrefersPriorityNow ? 'threshold-priority' : 'collision-priority';
          return { state: 'priority', overflowIndexes: indexes, reason: reasonPrefix + ':' + candidate.reason, requiredWidth: candidate.requiredWidth, availableWidth: available };
        }
      }

      return { state: 'menu', overflowIndexes: [], reason: lastMeasure.reason || 'menu-required', requiredWidth: lastMeasure.requiredWidth, availableWidth: available };
    }

    async function layout(reason, force) {
      if (destroyed || isCalculating) return;
      const available = externalWidth();
      if (!force && lastAvailableWidth && Math.abs(available - lastAvailableWidth) < WIDTH_CHANGE_THRESHOLD) {
        header.dataset.debugReason = 'skip-width-delta';
        return;
      }

      isCalculating = true;
      suppressObserver = true;
      header.dataset.debugObserverSuppressed = 'true';
      header.dataset.debugReason = reason || 'layout';

      try {
        lastAvailableWidth = available;
        const result = await computeTarget();
        result.reason = reason + ':' + result.reason;
        applyLayout(result);

        if (stateChanges.length > MAX_STATE_CHANGES_PER_SECOND) {
          console.warn('[TREEDIX Header v2] Rapid state changes detected; locking stable menu until next external resize.');
          applyLayout({ state: 'menu', overflowIndexes: [], reason: 'rapid-state-change-guard', requiredWidth: result.requiredWidth, availableWidth: available });
        }
      } catch (error) {
        console.warn('[TREEDIX Header v2] Layout calculation failed; keeping stable menu.', error);
        applyLayout({ state: 'menu', overflowIndexes: [], reason: 'calculation-error', requiredWidth: 0, availableWidth: available });
      } finally {
        isCalculating = false;
      }
    }

    function scheduleLayout(reason, delay, force) {
      if (destroyed || suppressObserver || isCalculating) return;
      if (scheduled && !force) return;
      scheduled = true;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        scheduled = false;
        layout(reason, force);
      }, delay == null ? 100 : delay);
    }

    function onResize() {
      scheduleLayout('external-resize', 110, false);
    }

    function onOrientationChange() {
      scheduleLayout('orientationchange', 180, true);
    }

    function onMobileChange() {
      scheduleLayout('mobile-media-change', 80, true);
    }

    function onDocumentClick(event) {
      if (!header.contains(event.target)) {
        closeOverflow();
        closeSubmenus(header);
      }
    }

    function onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        closeOverflow();
        closeSubmenus(header);
        closeDrawer();
      }
    }

    overflowButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (overflow.hidden) return;
      overflow.classList.toggle('is-open');
      overflowButton.setAttribute('aria-expanded', String(overflow.classList.contains('is-open')));
    });

    menuButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeOverflow();
      if (drawer.hidden) openDrawer();
      else closeDrawer();
    });

    header.addEventListener('click', (event) => {
      const submenuButton = event.target.closest('[data-header-v2-submenu-toggle]');
      if (submenuButton && header.contains(submenuButton)) {
        event.preventDefault();
        event.stopPropagation();
        const item = submenuButton.closest('.treedix-header-v2__nav-item');
        const willOpen = !item.classList.contains('is-submenu-open');
        closeSubmenus(header);
        item.classList.toggle('is-submenu-open', willOpen);
        submenuButton.setAttribute('aria-expanded', String(willOpen));
      }

      const drawerToggle = event.target.closest('[data-header-v2-drawer-toggle]');
      if (drawerToggle && header.contains(drawerToggle)) {
        event.preventDefault();
        const item = drawerToggle.closest('.treedix-header-v2__drawer-item');
        const submenu = item.querySelector('.treedix-header-v2__drawer-submenu');
        const willOpen = drawerToggle.getAttribute('aria-expanded') !== 'true';
        drawerToggle.setAttribute('aria-expanded', String(willOpen));
        item.classList.toggle('is-open', willOpen);
        if (submenu) submenu.hidden = !willOpen;
      }

      if (event.target.closest('[data-header-v2-drawer-close]')) {
        event.preventDefault();
        closeDrawer();
      }

      if (event.target.closest('a[href]')) {
        closeOverflow();
        closeDrawer();
      }
    });

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onOrientationChange, { passive: true });
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    disposers.push(() => window.removeEventListener('resize', onResize));
    disposers.push(() => window.removeEventListener('orientationchange', onOrientationChange));
    disposers.push(() => document.removeEventListener('click', onDocumentClick));
    disposers.push(() => document.removeEventListener('keydown', onDocumentKeydown));

    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener('change', onMobileChange);
      disposers.push(() => mobileMedia.removeEventListener('change', onMobileChange));
    } else if (mobileMedia.addListener) {
      mobileMedia.addListener(onMobileChange);
      disposers.push(() => mobileMedia.removeListener(onMobileChange));
    }

    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(() => {
        if (suppressObserver || isCalculating) return;
        const available = externalWidth();
        if (Math.abs(available - lastAvailableWidth) >= WIDTH_CHANGE_THRESHOLD) {
          scheduleLayout('observer-external-width-change', 100, false);
        }
      });
      observer.observe(document.documentElement);
    }

    controllers.set(header, {
      destroy() {
        destroyed = true;
        window.clearTimeout(resizeTimer);
        if (observer) observer.disconnect();
        disposers.forEach((dispose) => dispose());
        controllers.delete(header);
      }
    });

    waitForLogo(header)
      .then(() => document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve())
      .then(() => layout('initial', true))
      .then(() => scheduleMobileHextomLanguageSync(header))
      .catch(() => layout('initial-fallback', true));
  }

  function init(root) {
    (root || document).querySelectorAll(HEADER).forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
  document.addEventListener('shopify:section:unload', (event) => {
    (event.target || document).querySelectorAll(HEADER).forEach((header) => {
      const controller = controllers.get(header);
      if (controller) controller.destroy();
    });
  });
})();
